import React from 'react';

/**
 * FileCard Component
 * Displays file card matching the Omni Workspace Files design:
 * Thumbnail icon, filename, folder/project tag, version pill, and upload date.
 */
export default function FileCard({ file, isSelected, onClick, onPreview, onDownload, onPublishToCRE }) {
    const isPdf = file.type === 'PDF';
    const isSpreadsheet = ['XLSX', 'XLS', 'CSV', 'TSV'].includes(file.type);
    const isImage = ['PNG', 'JPG', 'JPEG', 'WEBP', 'SVG', 'AVIF', 'GIF'].includes(file.type);
    const isDesign = ['AI', 'PSD', 'FIG', 'SKETCH', 'XD', 'EPS', 'INDD'].includes(file.type);
    const isArchive = ['ZIP', 'RAR', '7Z', 'TAR'].includes(file.type);

    const getIconColorClass = () => {
        if (isPdf) return 'text-[#ba1a1a]';
        if (isSpreadsheet) return 'text-[#006a61]';
        if (isDesign) return 'text-[#a259ff]';
        if (isArchive) return 'text-[#6366f1]';
        return 'text-[#0070F3]';
    };

    const isDeliverable = file.folderCode === '05' || file.folderId?.includes('_05');
    const isCRESynced = file.publishedToCRE || !!file.creFileId;

    return (
        <div
            onClick={() => onClick?.(file)}
            className={`
                group relative bg-white dark:bg-[#1a2538]
                border border-[#c1c6d7] dark:border-[#344258]
                rounded-xl p-4 hover:shadow-md transition-all cursor-pointer
                ${isSelected ? 'ring-2 ring-[#0070F3] shadow-md border-transparent' : 'hover:border-[#94a3b8]'}
            `}
        >
            <div className="flex flex-col gap-3">
                {/* Thumbnail / Icon Container */}
                <div className="h-32 bg-[#eff4ff] dark:bg-[#212f45] rounded-lg flex items-center justify-center mb-1 relative overflow-hidden">
                    <span className={`material-symbols-outlined text-5xl transition-transform group-hover:scale-110 ${getIconColorClass()}`}>
                        {file.icon || 'description'}
                    </span>

                    {/* Top-left pills (CRE Synced / Revision) */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
                        {isCRESynced && (
                            <span
                                title={file.creFileId ? `Published to CRE: ${file.creFileId}` : 'Synced with CRE'}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[10px] font-bold shadow-xs backdrop-blur-xs"
                            >
                                <span className="material-symbols-outlined text-[11px]">cloud_done</span>
                                CRE Synced
                            </span>
                        )}
                        {file.revisionRound && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-500/30 text-[10px] font-bold shadow-xs backdrop-blur-xs">
                                <span className="material-symbols-outlined text-[11px]">history</span>
                                {file.revisionRound}
                            </span>
                        )}
                    </div>

                    {/* Quick hover action bar */}
                    <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isDeliverable && !isCRESynced && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPublishToCRE?.(file);
                                }}
                                title="Publish Deliverable to CRE"
                                className="p-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:scale-105 transition-all"
                            >
                                <span className="material-symbols-outlined text-base leading-none block">publish</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onPreview?.(file);
                            }}
                            title="Preview"
                            className="p-1.5 rounded-full bg-white/90 dark:bg-[#141c2b]/90 text-[#414754] dark:text-[#eaf1ff] hover:text-[#0070F3] shadow-sm hover:scale-105 transition-all"
                        >
                            <span className="material-symbols-outlined text-base leading-none block">visibility</span>
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDownload?.(file);
                            }}
                            title="Download"
                            className="p-1.5 rounded-full bg-white/90 dark:bg-[#141c2b]/90 text-[#414754] dark:text-[#eaf1ff] hover:text-[#0070F3] shadow-sm hover:scale-105 transition-all"
                        >
                            <span className="material-symbols-outlined text-base leading-none block">download</span>
                        </button>
                    </div>
                </div>

                {/* File info */}
                <div className="space-y-0.5">
                    <h3 className="font-semibold text-sm text-[#0b1c30] dark:text-[#eaf1ff] truncate" title={file.name}>
                        {file.name}
                    </h3>
                    <p className="text-xs text-[#727786] dark:text-[#9aa5bc] truncate">
                        {file.project || 'General'}
                    </p>
                </div>

                {/* Card footer: version + date */}
                <div className="flex justify-between items-center pt-2 border-t border-[#c1c6d7]/60 dark:border-[#344258]">
                    <div className="flex items-center gap-1.5">
                        <span className="px-2 py-0.5 bg-[#e6f8f5] dark:bg-[#0b3d37] text-[#006f66] dark:text-[#89f5e7] rounded-full text-[10px] font-bold">
                            {file.version || 'v1.0'}
                        </span>
                        {file.status === 'archived' && (
                            <span className="px-2 py-0.5 bg-amber-500/15 text-amber-600 dark:text-amber-400 rounded-full text-[10px] font-bold border border-amber-500/20">
                                Archived
                            </span>
                        )}
                    </div>
                    <span className="text-xs text-[#414754] dark:text-[#9aa5bc]">
                        {file.date || 'Recent'}
                    </span>
                </div>
            </div>
        </div>
    );
}
